import { Typography } from "@sidelinesports/fan-design-system";
import { ThinChevronRightIcon } from "@sidelinesports/icons";
import clsx from "clsx";
import { memo, useRef } from "react";
import type { ApiGameMessage } from "../../../../../api/index.ts";
import { IconLink } from "../../../../../components/IconLink.tsx";
import { UploadedImage } from "../../../../../components/UploadedImage.tsx";
import { useClickWithin } from "../../../../../hooks/use-click-within.ts";
import { useExternalLinkProps } from "../../../../../hooks/use-external-link-props.ts";
import styles from "./MessageItem.module.css";

type MessageItemProps = {
  message: ApiGameMessage;
};

const MessageItemUnmemoized = ({ message }: MessageItemProps) => {
  const containerRef = useRef(null);

  const handleClickWithin = useClickWithin(containerRef);
  const onClick = message.link ? handleClickWithin : undefined;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Click handles interior link
    <div
      ref={containerRef}
      onClick={onClick}
      onKeyUp={onClick}
      className={clsx(styles.container, message.link && styles["has-link"])}
    >
      {message.image && (
        <UploadedImage
          image={message.image}
          alt=""
          maxH="4rem"
          maxW="4rem"
          border="0.0625rem solid white"
          borderRadius={4}
          className={styles.image}
          tracking={{
            enabled: true,
            location: "LiveFeed::MessageItemImage",
          }}
        />
      )}
      <Typography size="md" className={styles.message}>
        {message.text}
      </Typography>
      {message.link && <LinkChrevron href={message.link} />}
    </div>
  );
};

export const MessageItem = memo(MessageItemUnmemoized);

const LinkChrevron = ({ href }: { href: string }) => {
  const linkProps = useExternalLinkProps(href, {
    utm: {
      content: "live-feed-message",
    },
  });

  if (!linkProps) {
    return null;
  }

  return (
    <IconLink {...linkProps}>
      <ThinChevronRightIcon fontSize="1.5rem" />
    </IconLink>
  );
};
